import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function TrendChart({
  data,
  metric,
  metricLabel,
  color,
  isLoading,
}) {
  const gradientId = `colorGradient-${metric}`;

  // Define reference ranges for different metrics
  const referenceRanges = {
    glucose: { min: 70, max: 100, unit: "mg/dL" },
    cholesterol_total: { min: 0, max: 200, unit: "mg/dL" },
    cholesterol_hdl: { min: 40, max: 100, unit: "mg/dL" },
    cholesterol_ldl: { min: 0, max: 100, unit: "mg/dL" },
    triglycerides: { min: 0, max: 150, unit: "mg/dL" },
    hemoglobin: { min: 12, max: 16, unit: "g/dL" },
    white_blood_cells: { min: 4500, max: 11000, unit: "/μL" },
  };

  const referenceRange = referenceRanges[metric] || {
    min: 0,
    max: 100,
    unit: "",
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-lg shadow-xl border border-slate-200">
          <p className="font-medium text-slate-900">{label}</p>
          <p className="text-blue-600">
            {`${metricLabel || metric.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}: ${payload[0].value} ${referenceRange.unit}`}
          </p>
          {payload[0].payload.test_type && (
            <p className="text-slate-500 text-sm">
              Test: {payload[0].payload.test_type.replace(/_/g, " ")}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <Skeleton className="h-6 w-32" />
          </CardTitle>
        </CardHeader>
        <CardContent className="h-96">
          <Skeleton className="w-full h-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            {metricLabel ||
              metric
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
            Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="h-96 flex items-center justify-center">
          <div className="text-center text-slate-500">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No data available for this metric</p>
            <p className="text-sm">Upload blood tests to see trends</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          {metricLabel ||
            metric
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
          Trend
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Tracking {data.length} test results over time
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#64748b"
                fontSize={12}
                tick={{ fill: "#64748b" }}
              />
              <YAxis
                stroke="#64748b"
                fontSize={12}
                tick={{ fill: "#64748b" }}
                domain={["dataMin - 5", "dataMax + 5"]}
              />

              {/* Reference range lines */}
              {referenceRange.max > 0 && (
                <ReferenceLine
                  y={referenceRange.max}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  label={{ value: "Max Normal", position: "topRight" }}
                />
              )}
              {referenceRange.min > 0 && (
                <ReferenceLine
                  y={referenceRange.min}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  label={{ value: "Min Normal", position: "bottomRight" }}
                />
              )}

              <Tooltip content={<CustomTooltip />} />

              <Line
                type="monotone"
                dataKey="value"
                stroke={color || "#3b82f6"}
                strokeWidth={3}
                dot={{
                  fill: color || "#3b82f6",
                  strokeWidth: 2,
                  r: 6,
                  stroke: "#ffffff",
                }}
                activeDot={{
                  r: 8,
                  stroke: color || "#3b82f6",
                  strokeWidth: 3,
                  fill: "#ffffff",
                }}
              />

              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={color || "#3b82f6"} />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
              </defs>
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color || "#3b82f6" }}
              ></div>
              <span>Your Values</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-red-500"></div>
              <span>Normal Range</span>
            </div>
          </div>
          <span>{referenceRange.unit && `Units: ${referenceRange.unit}`}</span>
        </div>
      </CardContent>
    </Card>
  );
}
